/**
 * [BLURY] | 🥭 encodeAvif
 * Main-thread side of the AVIF encoder: ships the pixels to a one-shot worker
 * and resolves with the encoded file.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import type { AvifRequest, AvifResponse } from '@/workers/avif.worker';

export const AVIF_MIME = 'image/avif';

export function encodeAvif(image: ImageData, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../workers/avif.worker.ts', import.meta.url), {
      type: 'module',
    });

    const finish = (fn: () => void) => {
      worker.terminate();
      fn();
    };

    worker.onmessage = (event: MessageEvent<AvifResponse>) => {
      const result = event.data;
      if (result.ok) {
        finish(() => resolve(new Blob([result.buffer], { type: AVIF_MIME })));
      } else {
        finish(() => reject(new Error(result.message)));
      }
    };

    worker.onerror = event => {
      finish(() => reject(new Error(event.message || 'the AVIF encoder failed to load')));
    };

    // The pixel buffer is transferred, not copied — it can be 100 MB+ at large
    // output widths.
    const request: AvifRequest = {
      buffer: image.data.buffer as ArrayBuffer,
      width: image.width,
      height: image.height,
      quality,
    };
    worker.postMessage(request, [request.buffer]);
  });
}
